import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import Group from 'components/utils/Group'
import FilterChip from 'LuxKit/Chips/FilterChip'
import { getCurrentDateLabel, getCurrentShortOccupancyLabel } from 'selectors/offerPage/offerPageSelectors'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import ModalContext from 'contexts/ModalContext'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import OfferCapacityWithAgesSelection from '../../Filters/CapacityWithAges/OfferCapacityWithAgesSelection'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import OfferSidebarDatePicker from 'components/OfferPage/Filters/SidebarDatePicker/OfferSidebarDatePicker'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'

interface Props {
  offer: App.HotelOffer
  dateLabel: string
  occupancyLabel: string
}

function AlwaysOnMobileStickyHeaderFilters(props: Props) {
  const {
    offer,
    dateLabel,
    occupancyLabel,
  } = props

  const intl = useIntl()
  const dispatch = useOfferPageDispatch()
  const showModal = useContext(ModalContext)

  const capacities = useMemo(() => {
    return offer.packages.flatMap((p) => p.roomRate.capacities)
  }, [offer.packages])

  const onClose = useCallback(() => {
    dispatch({ type: OfferPageActions.closeHeaderFilter })
  }, [dispatch])

  const onOpenCalendar = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleHeaderFilter, filter: 'checkin' })

    showModal((resolve) => <ModalBase mode="drawer">
      <form onSubmit={e => {
        e.preventDefault()
        resolve()
      }}
      >
        <OfferSidebarDatePicker />
      </form>
    </ModalBase>).then(onClose)
  }, [dispatch, showModal, onClose])

  const onOpenCapacity = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleHeaderFilter, filter: 'capacity' })

    showModal(<ModalBase mode="drawer">
      <ModalHeader title={intl.formatMessage({
        id: 'jwBgGniGf4EUlLOKeqlF',
        defaultMessage: 'Who is going?',
        description: 'Title for capacity selection modal in always-on mobile sticky header',
      })}
      />
      <ModalBody>
        <OfferCapacityWithAgesSelection capacities={capacities} />
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: intl.formatMessage({
            id: 'OnCgUMrzamjZsjlDLS_E',
            defaultMessage: 'Apply',
            description: 'Button to apply capacity selection in always-on mobile sticky header',
          }),
        }}
      />
    </ModalBase>).then(onClose)
  }, [dispatch, showModal, capacities, onClose, intl])

  return (
    <Group direction="horizontal" gap={8}>
      <FilterChip
        startIcon={<LineCalendarIcon />}
        size="medium"
        onClick={onOpenCalendar}
      >
        {dateLabel || intl.formatMessage({
          id: 'Jg7M7tHg-W6TftdF1dug',
          defaultMessage: 'Anytime',
          description: 'Default date filter label when no dates are selected in always-on mobile sticky header',
        })}
      </FilterChip>
      {occupancyLabel && <FilterChip
        startIcon={<LineUsersAltIcon />}
        size="medium"
        onClick={onOpenCapacity}
      >
        {occupancyLabel}
      </FilterChip>}
    </Group>
  )
}

export default withOfferPageAndReduxState((state) => ({
  offer: state.offer,
  dateLabel: getCurrentDateLabel(state),
  occupancyLabel: getCurrentShortOccupancyLabel(state),
}), AlwaysOnMobileStickyHeaderFilters)
